import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link2,
  ImageIcon,
  Minus,
  Undo2,
  Redo2,
  Palette,
  RemoveFormatting,
  Zap,
  ChevronDown,
  Quote,
  Youtube,
  Trash2,
  MousePointerClick,
  BarChart3,
} from "lucide-react";
import { editorColors as colors, fonts, MERGE_TAGS } from "../../styles/theme";
import { ToolBtn, ToolDivider } from "./ToolBtn";
import ColorPicker from "./ColorPicker";

export default function EditorToolbar({
  editor,
  currentBlock,
  handleBlockFormat,
  showColorPicker,
  setShowColorPicker,
  showMergeTags,
  setShowMergeTags,
  insertMergeTag,
  insertLink,
  handleInsertImage,
  handleInsertYouTube,
  handleInsertButton,
  handleInsertPoll,
  handleInsertWebcastLink,
  uploading,
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "8px 16px",
        background: colors.bgCard,
        borderBottom: `1px solid ${colors.border}`,
        flexWrap: "wrap",
        flexShrink: 0,
      }}
    >
      <ToolBtn
        icon={Undo2}
        label="Undo"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      />
      <ToolBtn
        icon={Redo2}
        label="Redo"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      />
      <ToolDivider />

      <select
        value={currentBlock}
        onChange={(e) => handleBlockFormat(e.target.value)}
        style={{
          padding: "4px 8px",
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: 4,
          color: colors.textSecondary,
          fontSize: 12,
          fontFamily: fonts.body,
          outline: "none",
          cursor: "pointer",
        }}
      >
        <option value="p">Paragraph</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="blockquote">Quote</option>
      </select>
      <ToolDivider />

      <ToolBtn
        icon={Bold}
        label="Bold (Ctrl+B)"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolBtn
        icon={Italic}
        label="Italic (Ctrl+I)"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolBtn
        icon={Underline}
        label="Underline (Ctrl+U)"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <ToolBtn
        icon={Strikethrough}
        label="Strikethrough"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />
      <ToolDivider />

      <div style={{ position: "relative" }}>
        <ToolBtn
          icon={Palette}
          label="Text Color"
          onClick={() => setShowColorPicker(!showColorPicker)}
        />
        {showColorPicker && (
          <ColorPicker
            onSelect={(color) =>
              editor.chain().focus().setColor(color).run()
            }
            onClose={() => setShowColorPicker(false)}
          />
        )}
      </div>
      <ToolDivider />

      <ToolBtn
        icon={AlignLeft}
        label="Align Left"
        active={editor.isActive({ textAlign: "left" })}
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
      />
      <ToolBtn
        icon={AlignCenter}
        label="Align Center"
        active={editor.isActive({ textAlign: "center" })}
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
      />
      <ToolBtn
        icon={AlignRight}
        label="Align Right"
        active={editor.isActive({ textAlign: "right" })}
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
      />
      <ToolDivider />

      <ToolBtn
        icon={List}
        label="Bullet List"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolBtn
        icon={ListOrdered}
        label="Numbered List"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolBtn
        icon={Quote}
        label="Blockquote"
        active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolDivider />

      <ToolBtn icon={Link2} label="Insert Link" onClick={insertLink} />
      <ToolBtn
        icon={ImageIcon}
        label="Insert Image"
        onClick={handleInsertImage}
        disabled={uploading}
      />
      <ToolBtn
        icon={Youtube}
        label="Embed YouTube Video"
        onClick={handleInsertYouTube}
      />
      <ToolBtn
        icon={MousePointerClick}
        label="Insert CTA Button"
        onClick={handleInsertButton}
      />
      <ToolBtn
        icon={BarChart3}
        label="Insert Poll"
        onClick={handleInsertPoll}
      />
      {editor?.isActive("image") && (
        <ToolBtn
          icon={Trash2}
          label="Delete selected image"
          onClick={() => editor.chain().focus().deleteSelection().run()}
        />
      )}
      <ToolBtn
        icon={Minus}
        label="Horizontal Rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      />
      <ToolDivider />

      <div style={{ position: "relative" }}>
        <button
          onClick={() => setShowMergeTags(!showMergeTags)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 10px",
            borderRadius: 5,
            border: `1px solid ${colors.border}`,
            background: colors.accentDim,
            color: colors.accent,
            fontSize: 11,
            fontWeight: 700,
            fontFamily: fonts.body,
            cursor: "pointer",
          }}
        >
          <Zap size={12} /> Merge Tags <ChevronDown size={10} />
        </button>
        {showMergeTags && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              marginTop: 4,
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: 6,
              minWidth: 240,
              boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
              zIndex: 200,
            }}
          >
            <div
              style={{
                padding: "6px 10px",
                fontSize: 10,
                color: colors.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: 700,
              }}
            >
              Insert Merge Tag
            </div>
            {MERGE_TAGS.map((mt) => (
              <button
                key={mt.tag}
                onClick={() => {
                  if (mt.needsEventId && handleInsertWebcastLink) {
                    handleInsertWebcastLink();
                  } else {
                    insertMergeTag(mt.tag);
                  }
                  setShowMergeTags(false);
                }}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 10px",
                  border: "none",
                  background: "transparent",
                  borderRadius: 4,
                  cursor: "pointer",
                  color: colors.text,
                  fontSize: 13,
                  fontFamily: fonts.body,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = colors.bgHover)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <span>{mt.label}</span>
                <code
                  style={{
                    fontSize: 11,
                    color: colors.accent,
                    fontFamily: fonts.mono,
                    background: colors.accentDim,
                    padding: "1px 6px",
                    borderRadius: 3,
                  }}
                >
                  {mt.tag}
                </code>
              </button>
            ))}
          </div>
        )}
      </div>

      <ToolBtn
        icon={RemoveFormatting}
        label="Clear Formatting"
        onClick={() =>
          editor.chain().focus().clearNodes().unsetAllMarks().run()
        }
      />
    </div>
  );
}
